import React from "react";
import { CLIENT_PROFILES, CLIENT_PROFILES_INTRO } from "@/lib/content";
import { Users, Briefcase, Globe2, Landmark, ArrowUpRight } from "lucide-react";

const ICONS = { Users, Briefcase, Globe2, Landmark };

const ClientProfile = () => {
  return (
    <section
      id="para-quien"
      data-testid="client-profile-section"
      className="relative bg-[#F9F6F0] text-[#0B0410] py-24 lg:py-32 border-t border-[#0B0410]/10 overflow-hidden"
    >
      {/* Subtle paper texture / tinted radial */}
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background:
            "radial-gradient(ellipse at 20% 0%, rgba(61,21,101,0.05) 0%, rgba(61,21,101,0) 60%), radial-gradient(ellipse at 80% 100%, rgba(230,213,184,0.35) 0%, rgba(230,213,184,0) 60%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#3D1565]" />
              <span
                className="text-[11px] tracking-luxury uppercase text-[#3D1565] font-medium"
                data-testid="client-profile-eyebrow"
              >
                {CLIENT_PROFILES_INTRO.eyebrow}
              </span>
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.08] text-[#0B0410]"
              data-testid="client-profile-headline"
            >
              {CLIENT_PROFILES_INTRO.headline.split("High Net Worth")[0]}
              <span className="italic text-[#3D1565]">High Net Worth</span>.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base lg:text-lg text-[#0B0410]/70 font-light">
              {CLIENT_PROFILES_INTRO.lead}
            </p>
          </div>
        </div>

        {/* Profiles grid */}
        <div className="grid md:grid-cols-2 gap-5 lg:gap-7">
          {CLIENT_PROFILES.map((p, idx) => {
            const Icon = ICONS[p.icon] || Users;
            return (
              <article
                key={p.title}
                data-testid={`client-profile-card-${idx}`}
                className="group relative bg-white border border-[#0B0410]/10 rounded-sm p-8 lg:p-10 hover:border-[#3D1565]/40 hover:shadow-[0_24px_60px_-30px_rgba(11,4,16,0.25)] transition-all duration-500 hover:-translate-y-0.5"
              >
                {/* Roman numeral */}
                <span className="absolute top-6 right-7 font-display text-2xl text-[#3D1565]/25">
                  {String.fromCharCode(8544 + idx)}
                </span>

                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#3D1565]/8 border border-[#3D1565]/25 rounded-sm">
                    <Icon
                      size={20}
                      className="text-[#3D1565]"
                      strokeWidth={1.6}
                    />
                  </div>
                </div>

                <h3 className="font-display text-2xl lg:text-3xl font-light leading-tight text-[#0B0410]">
                  {p.title}
                </h3>

                <p className="mt-4 text-sm lg:text-base text-[#0B0410]/70 leading-relaxed font-light">
                  {p.desc}
                </p>

                <div className="mt-7 pt-5 border-t border-[#0B0410]/8 flex flex-wrap gap-2">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      data-testid={`client-profile-tag-${idx}`}
                      className="text-[10px] tracking-luxury uppercase text-[#3D1565] bg-[#3D1565]/6 border border-[#3D1565]/15 px-3 py-1.5 rounded-full font-medium"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <ArrowUpRight size={18} className="text-[#3D1565]" />
                </div>
              </article>
            );
          })}
        </div>

        {/* Bottom note */}
        <div className="mt-14 border-t border-[#0B0410]/10 pt-10 grid md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-2">
            <div className="text-[11px] tracking-luxury uppercase text-[#3D1565] font-medium mb-3">
              ¿No te reconoces en ningún arquetipo?
            </div>
            <p className="text-sm text-[#0B0410]/70 font-light leading-relaxed max-w-2xl">
              No pasa nada. Estos son los perfiles donde nuestra metodología
              demuestra más impacto, pero atendemos a clientes con situaciones
              únicas todos los días. Una conversación inicial sin costo aclara
              si tiene sentido trabajar juntos.
            </p>
          </div>
          <div className="md:text-right">
            <button
              data-testid="client-profile-cta"
              onClick={() => {
                const el = document.getElementById("contacto");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 bg-[#0B0410] hover:bg-[#3D1565] text-[#F9F6F0] text-sm tracking-wider uppercase px-8 py-4 rounded-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              Conversemos
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientProfile;
